import React, { useMemo } from 'react';
import { CustomMarker } from 'components/Map';
import { Typography, Chip, Box } from '@mui/material';
import { WindArrowIcon } from '@wavetrak/theme';
import classNames from 'classnames';
import type { Marker } from 'leaflet';

import { Speed } from 'types/units';
import { NearbyStation } from 'types/windStations';
import { round } from 'lodash';
import styles from './MapWindStationPin.module.scss';

interface Props {
  data: NearbyStation;
  units: Speed;
  children: React.ReactNode;
  active: boolean;
  isOffline: boolean;
  size: number;
  position: {
    lat: number;
    lon: number;
  };
  eventHandlers: {
    popupopen: () => void;
    popupclose: () => void;
  };
  className: string;
  zIndexOffset: number;
  showPinWithWindSpeed: boolean;
  hasLiveWindPermissions: boolean;
}

const WindStationPinIcon = React.forwardRef(
  (
    {
      data,
      units,
      active,
      isOffline,
      size,
      children,
      position,
      className,
      zIndexOffset,
      eventHandlers,
      showPinWithWindSpeed,
      hasLiveWindPermissions,
    }: Props,
    ref: React.ForwardedRef<Marker>,
  ) => {
    const pinContent = useMemo(
      () => (
        <Typography className={styles.pinContent}>
          <Typography variant="callout1">{round(data?.lastObserved?.wind?.speed || 0)}</Typography>
          <Typography className={styles.pinWindUnits} variant="overline3">
            {units?.toLocaleLowerCase()}
          </Typography>
        </Typography>
      ),
      [data?.lastObserved?.wind?.speed, units],
    );

    const pinIcon = useMemo(() => {
      if (isOffline) {
        return <div className={styles.pinOffline} data-testid="map-wind-offline-pin" />;
      }

      if (!showPinWithWindSpeed || !hasLiveWindPermissions) {
        return (
          <Box
            className={classNames(styles.pinNoWindSpeed, active && styles.pinActive)}
            data-testid="map-wind-station-pin-no-reading"
          >
            <WindArrowIcon
              rotation={(data?.lastObserved?.wind?.direction || 0) - 180}
              size={16}
            />{' '}
          </Box>
        );
      }
      return (
        <Chip
          className={classNames(styles.pinWithWindSpeed, { [styles.pinActive]: active })}
          data-testid="map-wind-station-pin"
          label={pinContent}
          icon={
            <WindArrowIcon rotation={(data?.lastObserved?.wind?.direction || 0) - 180} size={16} />
          }
        />
      );
    }, [isOffline, showPinWithWindSpeed, active, pinContent, data?.lastObserved?.wind?.direction]);

    return (
      <CustomMarker
        icon={pinIcon}
        iconSize={[size, 32]}
        iconAnchor={[size / 2, 32 / 2]}
        // @ts-ignore - improper type from quiver-react of lat, lng LatLng
        position={position}
        map={undefined}
        usingPortal={false}
        className={className}
        zIndexOffset={zIndexOffset}
        eventHandlers={eventHandlers}
        ref={ref}
      >
        {children}
      </CustomMarker>
    );
  },
);

WindStationPinIcon.displayName = 'WindStationPinIcon';

export default WindStationPinIcon;
